import {
  useAeropayUser,
  useCheckoutRequirements,
  STALE_TIMES,
} from '@iheartjane/data-access/react-query';
import { LineItemsGrid } from '@iheartjane/menu';
import {
  Accordion,
  Box,
  Button,
  ButtonToggle,
  Drawer,
  Flex,
  InfoIcon,
  JanePayLogoWhite,
  Link,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import type { ReservationMode } from '@iheartjane/types';
import { formatCurrency } from '@iheartjane/utils';
import { useMemo, useState } from 'react';

import type { CheckoutPreferences } from '@jane/shared-ecomm/data-access';
import { useTipSettings } from '@jane/shared-ecomm/hooks';
import { useMenu } from '@jane/shared-ecomm/providers';
import { getJwt } from '@jane/shared/auth';
import { useJaneUser, useReservationSlots } from '@jane/shared/data-access';
// eslint-disable-next-line no-restricted-imports
import type { PendingCartProduct as tPendingCartProduct } from '@jane/shared/models';
import type { Cart, PendingCartProduct } from '@jane/shared/types';

import { TippingInput } from '../tippingInput/tippingInput';
import { CartToppers } from './CartToppers';
import { useFullyReplaceCart } from './hooks/useFullyReplaceCart';
import { useQuickCheckoutCart } from './hooks/useQuickCheckoutCart';
import { useSubmitQuickCheckout } from './hooks/useSubmitQuickCheckout';
import { QuickCartDrawerCard } from './QuickCartDrawerCard';
import { QuickCheckoutConsent } from './QuickCheckoutConsent';
import { QuickCheckoutCustomerDetails } from './QuickCheckoutCustomerDetails';
import type { DeliveryAddress } from './QuickCheckoutFulfillment';
import { QuickCheckoutFulfillment } from './QuickCheckoutFulfillment';
import { QuickCheckoutModal } from './QuickCheckoutModal';
import { QuickCheckoutStoreDetails } from './QuickCheckoutStoreDetails';

import styles from './MenuQuickCheckoutDrawer.module.css';

export const MenuQuickCheckoutDrawer = ({
  checkoutPreferences,
  createdCart,
  isDrawerOpen,
  setIsDrawerOpen,
}: {
  checkoutPreferences: CheckoutPreferences;
  createdCart: Cart;
  isDrawerOpen: boolean;
  setIsDrawerOpen: (isOpen: boolean) => void;
}) => {
  const jdid = localStorage.getItem('jdid');
  const isMobile = useMobileMediaQuery({});
  const {
    appInfo: { appMode },
    store,
  } = useMenu();
  const { cart, addCartTopper } = useQuickCheckoutCart({
    appMode,
    initialCart: createdCart,
    storeId: store.id,
  });
  const { fullyReplaceCart } = useFullyReplaceCart({
    cartUuid: cart.uuid,
  });
  const { submit, isCheckingOut, hasCheckoutError, resetCheckoutError } =
    useSubmitQuickCheckout();
  const [currentReservationMode, setCurrentReservationMode] = useState<string>(
    checkoutPreferences.reservation_mode
  );
  const storeAddress = `${store.name}, ${store.address}, ${store.city}, ${store.state} ${store.zip}`;

  /** CONSENT DATA */
  const jwt = getJwt();
  const {
    data: checkoutRequirementsData,
    isPending: isCheckoutRequirementsLoading,
  } = useCheckoutRequirements(
    {
      storeId: Number(store.id) || null,
      reservationMode: currentReservationMode,
      jwt,
    },
    // Quick checkout is signed-in only, so keep skipping the fetch without a jwt.
    { enabled: !!jwt }
  );
  const [consents, setConsents] = useState<Record<string, boolean>>({});

  const consentItems = useMemo(() => {
    const agreements =
      checkoutRequirementsData?.checkout_agreement_versions || [];
    return agreements
      .filter(
        (agreement) =>
          agreement.reservation_mode === currentReservationMode ||
          !agreement.reservation_mode
      )
      .map((agreement) => ({
        external_link: agreement.external_link,
        external_links: agreement.external_links || [],
        id: agreement.id,
        label: agreement.label,
        required: agreement.consent_requirement.includes('required'),
      }));
  }, [checkoutRequirementsData, currentReservationMode]);

  const requiredConsentsValid = consentItems
    .filter((item) => item.required)
    .every((item) => consents[String(item.id)]);

  const userConsentPayload = consentItems.map((item) => ({
    checkout_agreement_version_id: item.id,
    consent: !!consents[String(item.id)],
  }));

  /** DELIVERY ADDRESS DATA */
  const isDelivery = currentReservationMode === 'delivery';
  const deliveryLabel = store.reservation_mode_labels?.delivery ?? 'delivery';
  const { address: prefillAddress } = checkoutPreferences;
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddress>({
    city: prefillAddress.city || '',
    country_code: prefillAddress.country_code || '',
    lat: prefillAddress.lat || 0,
    lng: prefillAddress.lng || 0,
    state_code: prefillAddress.state_code || '',
    street: prefillAddress.street || '',
    street2: prefillAddress.street2 || '',
    zipcode: prefillAddress.zipcode || '',
  });
  const [isAddressGeocoded, setIsAddressGeocoded] = useState(
    !!prefillAddress.street
  );

  const isDeliveryAddressValid =
    !isDelivery ||
    (isAddressGeocoded &&
      !!deliveryAddress.street &&
      !!deliveryAddress.city &&
      !!deliveryAddress.zipcode);

  /** RESERVATION SLOT DATA */
  const [fulfillmentTime, setFulfillmentTime] = useState<undefined | string>(
    undefined
  );
  const [reservationMessage, setReservationMessage] = useState<{
    delivery: string | undefined;
    pickup: string | undefined;
  }>({ pickup: undefined, delivery: undefined });
  const [deliveryStartedAt, deliveryFinishedAt] =
    fulfillmentTime?.split('>') || [];

  const deliveryProps = {
    lat: checkoutPreferences.address.lat,
    lng: checkoutPreferences.address.lng,
    zipcode: checkoutPreferences.address.zipcode,
  };
  const { data: reservationSlotsData, isLoading: reservationSlotsLoading } =
    useReservationSlots(
      {
        reservationMode: currentReservationMode as ReservationMode,
        storeId: Number(store.id),
        ...((currentReservationMode as ReservationMode) === 'delivery'
          ? deliveryProps
          : {}),
      },
      { staleTime: STALE_TIMES.short }
    );

  /** PAYMENT DATA */
  const { data: janeUser, isPending: isJaneUserLoading } = useJaneUser();
  const { data: aeropayUser, isPending: isAeropayUserLoading } = useAeropayUser(
    {
      janeUserId: janeUser?.user?.id || null,
      jwt,
    }
  );

  const selectedBank =
    aeropayUser && aeropayUser?.bank_accounts.find((bank) => bank.is_selected);
  const defaultBank = selectedBank || aeropayUser?.bank_accounts[0];

  const applyJanePayServiceFee = Boolean(
    cart.jane_pay_service_fee_amount && cart.jane_pay_service_fee_amount > 0
  );

  const [tipAmount, setTipAmount] = useState<number>(0);
  const { showTip, tipOptions, tipSettings } = useTipSettings({
    reservationMode: currentReservationMode,
    pickupMode: 'pickup', //currently don't support curbside from quick checkout
    deliveryTipSettings: store.delivery_tip_settings || undefined,
    tipSettings: store.tip_settings || undefined,
    selectedPaymentMethod: 'jane_pay',
    tipPaymentMethods: ['jane_pay'],
  });
  const isPercentage = tipSettings?.variant === 'percentage';

  const total = Number(cart.order_total) + tipAmount;
  const totalToDisplay = applyJanePayServiceFee
    ? total + cart.jane_pay_service_fee_amount
    : total;

  return (
    <>
      <Drawer
        width={isMobile ? '95vw' : '540px'}
        open={isDrawerOpen}
        onRequestClose={() => setIsDrawerOpen(false)}
        background="grays-ultralight"
      >
        <Drawer.Header divider title="Express Checkout">
          {/* TO DO: hook this up */}
          <ButtonToggle
            ariaLabel="Fulfillment mode"
            variant="tertiary"
            value={currentReservationMode}
            onChange={(mode) => setCurrentReservationMode(mode)}
          >
            <ButtonToggle.Button key={2} value="delivery" label="Delivery" />
            <ButtonToggle.Button key={1} value="pickup" label="Pickup" />
          </ButtonToggle>
        </Drawer.Header>
        <Drawer.Content noPadding style={{ overflowY: 'auto' }}>
          <Flex flexDirection="column">
            {!isMobile && (
              <Flex gap={12} p={16} background="grays-ultralight" width="100%">
                <InfoIcon />
                <Typography variant="body-semibold">
                  Pre-filled from your previous order
                </Typography>
              </Flex>
            )}
            <Flex gap={16} flexDirection="column">
              {cart.products.map((product: PendingCartProduct) => (
                <QuickCartDrawerCard
                  key={`${product.id}-${product.price_id}`}
                  appMode={appMode}
                  cartUuid={cart.uuid || ''}
                  product={product as tPendingCartProduct}
                  reservationMode={currentReservationMode as ReservationMode}
                />
              ))}
            </Flex>
            <Accordion>
              <Drawer.ContentDivider className={styles['drawerDivider']} />
              <QuickCheckoutFulfillment
                currentReservationMode={currentReservationMode}
                deliveryAddress={deliveryAddress}
                deliveryMessageEnabled={store.delivery_message_enabled}
                fulfillmentTime={fulfillmentTime}
                pickupMessageEnabled={store.pickup_message_enabled}
                prefillAddress={prefillAddress}
                reservationMessage={reservationMessage}
                setDeliveryAddress={setDeliveryAddress}
                setIsAddressGeocoded={setIsAddressGeocoded}
                setFulfillmentTime={setFulfillmentTime}
                setReservationMessage={setReservationMessage}
                reservationSlotsData={reservationSlotsData?.reservation_slots}
                reservationSlotsLoading={reservationSlotsLoading}
                storeCountryCode={store.country_code}
              />

              <Drawer.ContentDivider className={styles['drawerDivider']} />
              <QuickCheckoutStoreDetails
                storeAddress={storeAddress}
                storePhone={store.phone}
              />

              <Drawer.ContentDivider className={styles['drawerDivider']} />
              <QuickCheckoutCustomerDetails janeUser={janeUser} />
            </Accordion>

            <Drawer.ContentDivider className={styles['drawerDivider']} />
            <CartToppers
              cartProducts={cart.products}
              onAddToCart={addCartTopper}
              store={store}
            />
            {consentItems.length > 0 && (
              <>
                <Drawer.ContentDivider className={styles['drawerDivider']} />
                <QuickCheckoutConsent
                  consentItems={consentItems}
                  consents={consents}
                  requiredConsentsValid={requiredConsentsValid}
                  setConsents={setConsents}
                />
              </>
            )}
          </Flex>
        </Drawer.Content>

        <Drawer.Footer>
          <Flex
            flexDirection="column"
            gap={16}
            width="100%"
            alignItems="center"
          >
            <Accordion.Item id="fee-breakdown" style={{ width: '100%' }}>
              <Accordion.Heading style={{ width: '100%' }}>
                <Flex flexDirection="row" alignItems="center" width="100%">
                  <Typography variant="h2-semibold" branded>
                    Estimated total
                  </Typography>
                  <Typography variant="h2-semibold" as="p" branded ml="auto">
                    {formatCurrency(totalToDisplay)}
                  </Typography>

                  <Flex ml={isMobile ? 8 : 16}>
                    <Accordion.Indicator iconSize="md" />
                  </Flex>
                </Flex>
              </Accordion.Heading>
              <Accordion.Content style={{ width: '100%' }}>
                <Box mt={16}>
                  <LineItemsGrid
                    discountTotal={cart.all_discounts_total}
                    deliveryFee={cart.delivery_fee_amount}
                    hasDeliveryAddress={Boolean(deliveryAddress.street)}
                    preDiscountSubtotal={cart.item_total}
                    salesTax={cart.sales_tax_amount}
                    selectedPaymentMethod="jane_pay"
                    serviceFee={cart.service_fee_amount}
                    showTip={showTip}
                    storeTax={cart.store_tax_amount}
                    janePayServiceFeeAmount={cart.jane_pay_service_fee_amount}
                    reservationMode={currentReservationMode as ReservationMode}
                    reservationModeLabel={
                      currentReservationMode === 'delivery'
                        ? deliveryLabel
                        : 'Pickup' //TODO: replace when implementing reservation availability hook
                    }
                    roundingAmount={cart.rounding_amount}
                    taxIncluded={cart.tax_included}
                    tipAmount={tipAmount}
                    deliveryLabel={deliveryLabel}
                  />
                  {showTip && (
                    <TippingInput
                      cartTotal={cart?.order_total || 0}
                      currentTipAmount={tipAmount}
                      isPercentage={isPercentage}
                      onTipChange={(amount) => setTipAmount(amount)}
                      tipOptions={tipOptions}
                    />
                  )}
                </Box>
              </Accordion.Content>
            </Accordion.Item>
            <Button
              onClick={() => {
                submit({
                  age_verification: checkoutPreferences.age_verification,
                  aeropay_bank_account_id: defaultBank?.bank_account_id || '',
                  customer: {
                    email: janeUser?.user.email || '',
                    external_user_id: null,
                    name: janeUser?.user.name || '',
                    phone: janeUser?.user.phone || '',
                  },
                  ...(isDelivery && {
                    delivery_address: deliveryAddress,
                  }),
                  delivery_finished_at: parseInt(deliveryFinishedAt),
                  delivery_started_at: parseInt(deliveryStartedAt),
                  j_device_id: jdid || '',
                  message: isDelivery
                    ? reservationMessage.delivery
                    : reservationMessage.pickup,
                  reservation_mode: currentReservationMode,
                  tip_amount: tipAmount,
                  uuid: cart.uuid || '',
                  user_consent: userConsentPayload,
                });
              }}
              label="Pay now with"
              endIcon={<JanePayLogoWhite width="96px" altText="Jane Pay" />}
              full
              disabled={
                !requiredConsentsValid ||
                !isDeliveryAddressValid ||
                isCheckoutRequirementsLoading ||
                reservationSlotsLoading ||
                isJaneUserLoading ||
                isAeropayUserLoading
              }
            />
            <Button
              onClick={() => fullyReplaceCart()}
              label="Full checkout"
              full
              variant="secondary"
            />
            <Link onClick={() => setIsDrawerOpen(false)}>
              Continue shopping
            </Link>
          </Flex>
        </Drawer.Footer>
      </Drawer>

      <QuickCheckoutModal
        isOpen={isDrawerOpen && (isCheckingOut || hasCheckoutError)}
        isCheckingOut={isCheckingOut}
        hasCheckoutError={hasCheckoutError}
        onClose={() => setIsDrawerOpen(false)}
        fullyReplaceCart={fullyReplaceCart}
        resetCheckoutError={resetCheckoutError}
      />
    </>
  );
};
